import { useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Banner, Button, Card, Field } from '@cowoo/shared/ui-web';
import { ApiProblem, billingProfileSchema, type BillingProfileForm } from '@cowoo/shared';
import { api } from '../../lib/api';

/**
 * `/angaben` — the billing profile (09-web-member § 6, BR-ACC-03…05).
 *
 * Required before **any** checkout, including the 0 € trial day (D-063). The
 * 18+ rule is enforced by the server; the client only mirrors it so the user
 * finds out before the payment sheet.
 */
export function Details() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const form = useForm<BillingProfileForm>({
    resolver: zodResolver(billingProfileSchema),
    defaultValues: { kind: 'private', country: 'DE' },
  });

  const save = useMutation({
    mutationFn: async (values: BillingProfileForm) => {
      const { data, error } = await api.PUT('/me/billing-profile', { body: values });
      if (error) throw error;
      return data;
    },
    onSuccess: () => navigate('/zahlung'),
    onError: (error) => {
      // 422 field errors are merged back into the form (04 § 12.3).
      if (error instanceof ApiProblem) {
        for (const fieldError of error.fieldErrors) {
          const path = fieldError.path.replace(/^body\./, '') as keyof BillingProfileForm;
          form.setError(path, {
            message: t(`validation.${fieldError.code}`, { defaultValue: t('errors.validation_failed') }),
          });
        }
      }
    },
  });

  const kind = form.watch('kind');
  const message = (name: keyof BillingProfileForm): string | null => {
    const error = form.formState.errors[name];
    return error?.message ? t(error.message) : null;
  };

  return (
    <main className="mx-auto max-w-2xl px-5 py-12">
      <h1 className="font-heading text-ink mb-2 text-3xl">{t('member.details.title')}</h1>
      <p className="font-body text-text mb-6">{t('member.details.body')}</p>

      <Card>
        <form onSubmit={form.handleSubmit((values) => save.mutate(values))} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span className="font-body text-muted text-xs font-semibold uppercase tracking-wider">
              {t('member.details.kind')}
            </span>
            <div className="flex gap-4">
              {(['private', 'company'] as const).map((value) => (
                <label key={value} className="font-body text-ink flex items-center gap-2">
                  <input type="radio" value={value} {...form.register('kind')} />
                  {t(`member.details.kind_${value}`)}
                </label>
              ))}
            </div>
          </div>

          <Field
            label={t('member.details.firstName')}
            error={message('firstName')}
            {...form.register('firstName')}
          />
          <Field
            label={t('member.details.lastName')}
            error={message('lastName')}
            {...form.register('lastName')}
          />
          <Field
            label={t('member.details.dateOfBirth')}
            type="date"
            error={message('dateOfBirth')}
            {...form.register('dateOfBirth')}
          />
          <Field label={t('member.details.street')} error={message('street')} {...form.register('street')} />
          <Field
            label={t('member.details.postalCode')}
            error={message('postalCode')}
            {...form.register('postalCode')}
          />
          <Field label={t('member.details.city')} error={message('city')} {...form.register('city')} />

          {kind === 'company' ? (
            <>
              <Field
                label={t('member.details.companyName')}
                error={message('companyName')}
                {...form.register('companyName')}
              />
              {/* D-047 — format-checked only; we do not validate against VIES. */}
              <Field label={t('member.details.vatId')} error={message('vatId')} {...form.register('vatId')} />
            </>
          ) : null}

          <Button type="submit" loading={save.isPending} full>
            {t('common.continue')}
          </Button>
          {save.isError && save.error instanceof ApiProblem && save.error.fieldErrors.length === 0 ? (
            <Banner tone="danger">{t(save.error.i18nKey)}</Banner>
          ) : null}
        </form>
      </Card>
    </main>
  );
}
